/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { CKBOX_VERSION, CKEDITOR5_VERSION } from '@ckeditor/ckeditor5-metadata';
import { groupByInstallPackage } from '../groupByInstallPackage/groupByInstallPackage';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { getUsersIntegrationPlugin } from '../getUsersIntegrationPlugin/getUsersIntegrationPlugin';
import { getCommentsIntegrationPlugin } from '../getCommentsIntegrationPlugin/getCommentsIntegrationPlugin';
import {
	getRevisionHistoryIntegrationPlugin
} from '../getRevisionHistoryIntegrationPlugin/getRevisionHistoryIntegrationPlugin';
import { getTrackChangesIntegrationPlugin } from '../getTrackChangesIntegrationPlugin/getTrackChangesIntegrationPlugin';
import { getConfigUpdateAlert } from '../getConfigUpdateAlert/getConfigUpdateAlert';
import { getTokenVariables } from '../getTokenVariables/getTokenVariables';
import { getDefaultHexColors } from '../getDefaultHexColors/getDefaultHexColors';
import { getViewChildRefs } from './getViewChildRefs';
import { getOnReady } from './getOnReady';
import { areTranslationsNeeded } from '../translations/translations';
import type { GetAngularSourceTemplateArgs } from './types';
import type { EditorLanguage } from '../../components/LanguageSelection/languages';
import { requiresDocumentOutlineContainer } from '../requiresDocumentOutlineContainer/requiresDocumentOutlineContainer';
import { requiresCommentsContainer } from '../requiresCommentsContainer/requiresCommentsContainer';
import { getPluginsImport } from '../getPluginsImport/getPluginsImport';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';
import { getBuilderUrlComment } from '../getBuilderUrlComment/getBuilderUrlComment';

export function getAngularSourceTemplateCdn( {
	customerPortalAccount,
	editorConfig,
	editorOptions,
	evaluationLicense,
	selectedInstallMethod,
	builderUrl
}: GetAngularSourceTemplateArgs ): string {
	const { editorPlugins, toolbarOptions, editorType, editorLanguage } = editorOptions;

	const groupedPlugins = groupByInstallPackage( editorPlugins );
	const hasPremiumPlugins = groupedPlugins[ 'ckeditor5-premium-features' ].length > 0;
	const viewChildRefs = getViewChildRefs( editorPlugins, toolbarOptions, editorType );
	const shouldImportButtonView = isDecoupledEditor( editorType ) &&
		( requiresDocumentOutlineContainer( editorPlugins ) || requiresCommentsContainer( editorPlugins ) );
	const configUpdateAlert = getConfigUpdateAlert( {
		editorPlugins,
		editorConfig,
		configRef: 'this.config',
		lang: 'ts'
	} );
	const { importsFromCKEditor, importsFromCKEditorPremiumFeatures } = getPluginsImport( editorType, groupedPlugins );

	return `
		${ getBuilderUrlComment( builderUrl ) }

		${ getAngularImports( !!viewChildRefs ) }
		import { CommonModule } from '@angular/common';
		import { loadCKEditorCloud, CKEditorModule, type CKEditorCloudResult, type CKEditorCloudConfig } from '@ckeditor/ckeditor5-angular';

		import type {
			${ editorType },
			EditorConfig,
			${ shouldImportButtonView ? 'ButtonView as ButtonViewType' : '' }
		} from "https://cdn.ckeditor.com/typings/ckeditor5.d.ts";

		${ getTokenVariables( { editorConfig, editorPlugins, customerPortalAccount, evaluationLicense, selectedInstallMethod } ) }

		${ getDefaultHexColors( editorPlugins ) }

		const cloudConfig = ${ getCloudConfig( editorLanguage, editorPlugins, hasPremiumPlugins ) } satisfies CKEditorCloudConfig;

		@Component( {
			selector: 'app-root',
			standalone: true,
			imports: [ CommonModule, CKEditorModule ],
			templateUrl: './app.component.html',
			styleUrls: [ './app.component.css' ],
			encapsulation: ViewEncapsulation.None
		} )
		export class AppComponent implements OnInit {
			${ viewChildRefs }

			public Editor: typeof ${ editorType } | null = null;
			public config: EditorConfig | null = null;

			public ngOnInit(): void {
				loadCKEditorCloud( cloudConfig ).then( this._setupEditor.bind( this ) );
			}

			private _setupEditor( cloud: CKEditorCloudResult<typeof cloudConfig> ) {
				const {
					${ importsFromCKEditor.join( ', ' ) }
				} = cloud.CKEditor;

				${ hasPremiumPlugins ? getPremiumPlugins( importsFromCKEditorPremiumFeatures ) : '' }

				${ getUsersIntegrationPlugin( editorPlugins ) }

				${ getCommentsIntegrationPlugin( editorPlugins ) }

				${ getTrackChangesIntegrationPlugin( editorPlugins ) }

				${ getRevisionHistoryIntegrationPlugin( editorPlugins ) }

				this.Editor = ${ editorType };
				this.config = ${ editorConfig };

				${ configUpdateAlert.call }
			}

			${ getOnReady( editorType, toolbarOptions, editorPlugins ) }
		}

		${ configUpdateAlert.fn }
	`;
}

function getCloudConfig( editorLanguage: EditorLanguage, editorPlugins: Array<string>, hasPremiumPlugins: boolean ) {
	const isStableRelease = /^\d+\.\d+\.\d+$/.test( CKEDITOR5_VERSION );

	const config = JSON.stringify( {
		version: CKEDITOR5_VERSION,
		...areTranslationsNeeded( editorLanguage ) ? { translations: [ editorLanguage ] } : {},
		...editorPlugins.includes( 'CKBox' ) ? { ckbox: { version: CKBOX_VERSION } } : {},
		...hasPremiumPlugins ? { premium: true } : {}
	}, null, 4 );

	if ( isStableRelease ) {
		return config;
	}

	return config.replace(
		`"${ CKEDITOR5_VERSION }"`,
		`"${ CKEDITOR5_VERSION }" as any /* The casting is used here for testing purposes because it is a non-stable release. */`
	);
}

function getPremiumPlugins( importsFromCKEditorPremiumFeatures: EditorPlugins ) {
	return `const { ${ importsFromCKEditorPremiumFeatures.join( ', ' ) } } = cloud.CKEditorPremiumFeatures;`;
}

function getAngularImports( shouldIncludeRefImports: boolean ) {
	const imports = [
		'Component',
		'ViewEncapsulation',
		'type OnInit',
		shouldIncludeRefImports ? 'ElementRef' : '',
		shouldIncludeRefImports ? 'ViewChild' : ''
	].filter( Boolean ).join( ', ' );

	return `import { ${ imports } } from '@angular/core';`;
}
